<template>
  <div id="app-container" :class="containerClass">
    <AppTopBar
      ref="topBar"
      @menu-toggle="onMenuToggle()"
      @logout="onLogout()"
      @edit-profile="onEditProfile()"
      :pageTitle="$route.meta.pageTitle || 'Web Farm'"
      :searchBar="$route.meta.searchBar"
      :userName="userName"
    />

    <transition name="layout-sidebar">
      <div
        class="layout-sidebar layout-sidebar-light"
        @click="onSidebarClick"
        v-show="isSidebarVisible()"
        :style="{ marginTop: `${topBarHeight}px` }"
      >
        <div class="layout-logo">
          <router-link to="/">
            <img alt="Logo" src="../public/web-farm-logo.svg" />
          </router-link>
        </div>
        <AppMenu />
      </div>
    </transition>

    <Toast position="top-center" :baseZIndex="1500"></Toast>

    <div class="layout-main" :style="{ paddingTop: `${topBarHeight + 20}px` }">
      <AppBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb"
      />
      <span v-if="setupFinished">
        <router-view />
      </span>
    </div>
    <AppTrialBar v-if="isTrial" :dueDate="accountDueDate"></AppTrialBar>

    <AppProfileEdit
      ref="profileEdit"
      v-if="editProfileVisible"
      v-model:visible="editProfileVisible"
      :showStatus="false"
      @saved="user = { ...user, ...$event }"
    />

    <UseTermDialog></UseTermDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import AppTopBar from './components/layout/AppTopbar.vue';
import AppMenu from './components/layout/AppMenu.vue';
import AppBreadcrumb from './components/layout/AppBreadcrumb.vue';
import AppTrialBar from './components/layout/AppTrialBar.vue';
import useResizeObserver from './lib/useResizeObserver';
import AppProfileEdit from './views-admin/User/UserEdit';
import UseTermDialog from './views/Terms/components/UseTermDialog';

export default {
  components: {
    AppTopBar,
    AppMenu,
    AppBreadcrumb,
    AppTrialBar,
    AppProfileEdit,
    UseTermDialog,
  },
  data() {
    return {
      mobileMenuActive: false,
      menu: [],
      topBarHeight: 50,
      setupFinished: false,
      editProfileVisible: false,
    };
  },

  computed: {
    ...mapFields('app', [
      'user',
      'menuInactive',
      'loadingCursor',
    ]),
    ...mapGetters('app', ['isTrial', 'accountDueDate', 'permissions']),
    containerClass() {
      return [
        'layout-wrapper',
        'layout-static',
        'p-ripple-disabled',
        {
          'layout-static-sidebar-inactive': this.menuInactive,
          'layout-mobile-sidebar-active': this.mobileMenuActive,
          'layout-loading-cursor': this.loadingCursor,
        },
      ];
    },
    userName() {
      const userName = this.user.name || this.user.username;
      if (this.user?.account?.planId === 4 && this.user?.account?.inviteCode) {
        return `#${this.user?.account?.inviteCode.toUpperCase()} - ${userName}`;
      }
      return userName;
    },
  },

  created() {
    this.loadSession();
    this.$ability.update(this.permissions);

    if (!this.$ability.can('manage', 'App') && !this.$ability.can('manage', 'Admin')) {
      this.$router.push({ name: 'login' });
    }

    this.setupFinished = true;
  },

  watch: {
    $route() {
      this.menuActive = false;
      this.$toast.removeAllGroups();
    },
    permissions() {
      this.$ability.update(this.permissions);
    }
  },

  mounted() {
    const { height } = useResizeObserver(this.$refs.topBar.$el);
    this.topBarHeight = height;
  },

  methods: {
    ...mapActions('app', ['loadSession', 'logout']),
    onMenuToggle() {
      this.menuClick = true;
      if (this.isDesktop()) {
        this.menuInactive = !this.menuInactive;
      } else {
        this.mobileMenuActive = !this.mobileMenuActive;
      }
      event.preventDefault();
    },
    onLogout() {
      this.logout();
      this.$router.push({ name: 'login' });
    },
    onEditProfile() {
      this.editProfileVisible = true;
      this.$nextTick(() => {
        this.$refs.profileEdit.setData({ ...this.user });
      });
    },
    onSidebarClick() {
      this.menuClick = true;
    },
    addClass(element, className) {
      if (element.classList) {
        element.classList.add(className);
      } else {
        element.className += ' ' + className;
      }
    },
    removeClass(element, className) {
      if (element.classList) {
        element.classList.remove(className);
      } else {
        const pattern = '(^|\\b)' + className.split(' ').join('|') + '(\\b|$)';
        element.className = element.className.replace(
          new RegExp(pattern, 'gi'),
          ' '
        );
      }
    },
    isDesktop() {
      return window.innerWidth > 1024;
    },
    isSidebarVisible() {
      if (this.isDesktop()) {
        return !this.menuInactive;
      } else {
        return true;
      }
    },
  },
  beforeUpdate() {
    if (this.mobileMenuActive) {
      this.addClass(document.body, 'body-overflow-hidden');
    } else {
      this.removeClass(document.body, 'body-overflow-hidden');
    }
  },
};
</script>

<style lang="scss">
.p-toast.p-toast-topright {
  z-index: 1000;
  top: 70px;
}
</style>
